import { useEffect, useRef } from "react";

/**
 * Escape-to-dismiss for overlays on web (modals, dialogs, the header menu).
 *
 * Overlays register while open; one document-level keydown listener closes
 * only the top-most (most recently opened) one per Escape press. On native
 * there is no `document`, so registration is inert.
 *
 * Stacking order follows effect registration order. React runs child effects
 * before parent effects, so a parent and a nested modal that become visible in
 * the same commit would close the parent first. Current flows open nested
 * dialogs after their parent, so this does not happen today.
 */
type Entry = { dismiss: () => void };

const stack: Entry[] = [];
let listening: Document | null = null;

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key !== "Escape" && event.key !== "Esc") return;
  const top = stack[stack.length - 1];
  if (!top) return;
  event.preventDefault?.();
  event.stopPropagation?.();
  top.dismiss();
};

const sync = () => {
  const doc = typeof document === "undefined" ? null : document;
  if (stack.length > 0 && !listening && doc?.addEventListener) {
    doc.addEventListener("keydown", onKeyDown);
    listening = doc;
  } else if (stack.length === 0 && listening) {
    listening.removeEventListener("keydown", onKeyDown);
    listening = null;
  }
};

export function useEscapeToDismiss(
  active: boolean,
  onDismiss?: () => void,
): void {
  const latest = useRef(onDismiss);
  useEffect(() => {
    latest.current = onDismiss;
  });

  const enabled = active && typeof onDismiss === "function";
  useEffect(() => {
    if (!enabled) return undefined;
    const entry: Entry = { dismiss: () => latest.current?.() };
    stack.push(entry);
    sync();
    return () => {
      const i = stack.indexOf(entry);
      if (i !== -1) stack.splice(i, 1);
      sync();
    };
  }, [enabled]);
}
